import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  ArrowLeftIcon,
  CopyIcon,
  DownloadIcon,
  FileTextIcon,
  InfoIcon,
  OctagonAlertIcon,
  PenLineIcon,
  PrinterIcon,
  Share2Icon,
  TriangleAlertIcon,
} from "lucide-react"
import type * as React from "react"
import { useState } from "react"
import { panFromGstin } from "@steelbill/types"
import { useTranslation } from "react-i18next"
import { Link, useParams } from "react-router-dom"
import { toast } from "sonner"
import { SyncStatus } from "@/components/SyncStatus"
import { CreditNotesOnInvoice } from "@/features/credit-notes/CreditNotesOnInvoice"
import { DocumentStatusBadge } from "@/features/invoices/DocumentStatusBadge"
import { EwayBillCard } from "@/features/invoices/EwayBillCard"
import { InvoicePaymentBadge, PaymentsCard } from "@/features/payments/PaymentsCard"
import { useCancelInvoice, useInvoice, useInvoicePdf } from "@/features/invoices/use-invoice"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu"
import { DateField } from "@/components/DateField"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/features/auth/auth-context"
import { ApiError } from "@/lib/api-error"
import { api, requestBlob } from "@/lib/api"
import { shareDocument } from "@/lib/share"
import { useFormatDay } from "@/lib/dates"
import {
  endpoints,
  type DocumentLine,
  type InvoiceDetail,
  type PreviewEnvelope,
} from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { queryKeys } from "@/lib/query-keys"
import { routes } from "@/routes/paths"

/**
 * One bill (§4, §7, §11.3, §17.4).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS SCREEN NEVER OFFERS TO EDIT AN ISSUED BILL. Not disabled, not hidden
 * behind a confirmation — ABSENT. §2.1 does not define `invoice.edit` as a
 * capability for anyone, deliberately, because not creating it is stronger than
 * granting it to nobody. The legal correction paths are a credit note (§17.3) or
 * a cancellation (§17.4), and "Make a copy" is what the owner actually wants
 * nine times out of ten (§11.3).
 *
 * THE ONE EXCEPTION IS NOT AN EXCEPTION. A DRAFT bill — one that conversion
 * raised before the customer's S.O. number had arrived (§7.1) — can have that
 * number filled in, through `PUT /invoices/:id` and the SEPARATE
 * `invoice.edit_draft` capability. It changes no line, no total, no date, no
 * customer and no number; it completes what conversion left missing on a
 * document that has not been issued to anybody. See {@link DraftHeaderCard}.
 *
 * EVERY FIGURE COMES FROM THE SERVER. No total on this page is added up here —
 * §6 makes the server the only authority, and `totals.amountInWords` is rendered
 * server-side too so the screen and the PDF cannot word the same number
 * differently. The one arithmetic operation in this file is `qtyMilli / 1000`
 * for display, and it is commented where it happens.
 *
 * RATES ARE THE SNAPSHOT, NOT TODAY'S. `line.gstRateBp` was frozen when the
 * document was saved (§8.7). Rendering today's rate for the HSN code would make
 * an old invoice appear to disagree with its own arithmetic the day a rate
 * changes.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/**
 * The navigation-menu triggers, dressed as outline Buttons.
 *
 * They sit in a row beside "View the bill" and must read as the same KIND of
 * control. Left alone they do not: the stock trigger is `h-9` with no border
 * and no `gap`, so it is simultaneously TALLER than the `h-8` Button beside it
 * and lighter than it — which reads as smaller, because weight carries more
 * than height in a row. The missing `gap` is what ran the printer icon into
 * "Print a copy" with no space between them.
 *
 * Matched against `buttonVariants({ variant: "outline" })` field for field:
 * height, gap, padding, border, background and hover.
 */
const TRIGGER_AS_BUTTON =
  "h-8 gap-1.5 rounded-lg border border-border bg-background px-2.5 hover:bg-muted hover:text-foreground data-popup-open:bg-muted data-open:bg-muted"

export function InvoiceDetailScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const { can } = useAuth()
  const formatDay = useFormatDay()
  const queryClient = useQueryClient()

  const query = useInvoice(id)
  const invoice = query.data
  const pdf = useInvoicePdf(id, invoice?.number)

  const [sharing, setSharing] = useState(false)
  const onShare = async () => {
    if (invoice === undefined || id === undefined) return
    setSharing(true)
    try {
      const filename = `${invoice.number.replace(/\//g, "-")}.pdf`
      const fetched = await requestBlob(endpoints.invoicePdf(id), { fallbackFilename: filename })
      const outcome = await shareDocument({
        title: t("share.titleInvoice", { number: invoice.number }),
        text: t("share.messageInvoice", {
          number: invoice.number,
          amount: formatRupees(invoice.totals.grandTotalPaise),
        }),
        file: { blob: fetched.blob, filename: fetched.filename ?? filename },
        phone: invoice.customer?.phone ?? null,
      })
      if (outcome.status === "fallback") toast.info(t("share.openedWhatsapp"))
      if (outcome.status === "failed") toast.error(t("share.noShareTarget"))
      // "shared" needs no toast — the share sheet was its own confirmation —
      // and "dismissed" is him changing his mind, not an event.
    } catch {
      toast.error(t("share.couldNotPrepare"))
    } finally {
      setSharing(false)
    }
  }

  if (query.isPending) return <DetailSkeleton />

  if (query.isError) {
    const notFound = query.error instanceof ApiError && query.error.status === 404
    return (
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <BackLink />
        <Alert variant="destructive">
          <AlertTitle>{notFound ? t("err.notFound") : t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {query.error instanceof ApiError ? query.error.message : t("err.somethingWentWrong")}
            </span>
            {notFound ? null : (
              <Button
                variant="outline"
                onClick={() => {
                  void query.refetch()
                }}
              >
                {t("btn.retry")}
              </Button>
            )}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (invoice === undefined) return <DetailSkeleton />

  const cancelled = invoice.status === "CANCELLED"

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <BackLink />

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight tabular-nums">{invoice.number}</h1>
            <DocumentStatusBadge status={invoice.status} kind="invoice" />
            {/*
              * §17.5 — "Issued · Partly paid", two badges, never one. Payment is
              * a fact about the BANK and the status is a fact about the tax
              * document; replacing one with the other would delete the word that
              * says the bill stands. Renders nothing when the module is off,
              * when the caller lacks `payment.view`, or when nothing is owed.
              */}
            <InvoicePaymentBadge invoiceId={invoice.id} />
            {invoice.issuedManually ? (
              <span className="text-muted-foreground inline-flex items-center gap-1 text-xs">
                <PenLineIcon aria-hidden="true" className="size-3" />
                {t("invoice.writtenByHand")}
              </span>
            ) : null}
          </div>
          <p className="text-muted-foreground text-sm">
            {invoice.customer?.name ?? t("lbl.customerUnknown")} · {formatDay(invoice.date)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/*
            * HOW FRESH IS THIS? The bill on screen came from a cache, and a
            * cache is invisible — his son can cancel a bill from the shop
            * computer and this page would go on showing it as ISSUED until
            * something refetched. `refetch()` rather than invalidating the
            * whole cache: he asked about THIS bill, not the register.
            */}
          <SyncStatus
            updatedAt={query.dataUpdatedAt}
            syncing={query.isFetching}
            onSync={() => {
              void query.refetch()
            }}
          />

          {/*
            * THE PRIMARY ACTION STAYS A BUTTON. Opening the bill is what he
            * came here to do nine times out of ten, and burying it one click
            * inside a menu to make the row tidy would be the wrong trade.
            */}
          <Button
            onClick={() => {
              pdf.mutate(undefined)
            }}
            disabled={pdf.isPending}
          >
            <FileTextIcon />
            {pdf.isPending ? t("msg.loading") : t("btn.viewBill")}
          </Button>

          {/*
            * A NAVIGATION MENU, NOT A MENUBAR. The menubar drew both triggers
            * inside one bordered pill, so "Print a copy" and "Share" read as a
            * single segmented control rather than two independent menus — and
            * nothing on it said it would open anything. Each trigger stands on
            * its own here and carries a chevron, which is the whole difference:
            * it looks like something that expands, because it is.
            *
            * The statutory copies keep their §9.2 names beside the plain ones —
            * "Duplicate" on its own means nothing to anyone who has not read
            * the act, and "Transporter copy" means nothing to a GST officer.
            */}
          <NavigationMenu>
            {/* `gap-2` to match the row: NavigationMenuList ships `gap-0`, which
                * pushed "Print a copy" and "Share" flat against each other and
                * made two separate menus read as one control again — the exact
                * thing dropping the menubar was meant to fix. */}
            <NavigationMenuList className="gap-2">
              <NavigationMenuItem>
                <NavigationMenuTrigger className={TRIGGER_AS_BUTTON} disabled={pdf.isPending}>
                  <PrinterIcon className="size-4" />
                  {t("doc.copiesMenu")}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="flex w-56 flex-col">
                    {(
                      [
                        ["ORIGINAL", "doc.originalCopy"],
                        ["DUPLICATE", "doc.duplicateCopy"],
                        ["TRIPLICATE", "doc.triplicateCopy"],
                      ] as const
                    ).map(([copy, key]) => (
                      <li key={copy}>
                        {/*
                          * `render` because these ACT rather than navigate —
                          * they fetch a PDF. A bare NavigationMenuLink would
                          * emit an anchor with no href, which is neither a
                          * button nor a link to anything.
                          */}
                        <NavigationMenuLink
                          render={
                            <button
                              type="button"
                              className="w-full text-left"
                              onClick={() => {
                                pdf.mutate(copy)
                              }}
                            />
                          }
                        >
                          {t(key)}
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className={TRIGGER_AS_BUTTON} disabled={sharing}>
                  <Share2Icon className="size-4" />
                  {sharing ? t("share.preparing") : t("btn.share")}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="flex w-56 flex-col">
                    <li>
                      <NavigationMenuLink
                        render={
                          <button
                            type="button"
                            className="w-full text-left"
                            onClick={() => {
                              void onShare()
                            }}
                          />
                        }
                      >
                        <Share2Icon />
                        {t("btn.shareWhatsapp")}
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink
                        render={
                          <button
                            type="button"
                            className="w-full text-left"
                            onClick={() => {
                              pdf.mutate(undefined)
                            }}
                          />
                        }
                      >
                        <DownloadIcon />
                        {t("btn.downloadPdf")}
                      </NavigationMenuLink>
                    </li>
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          {/*
            * ITS OWN BUTTON, NOT A SHARE ITEM. Copying does not send this bill
            * anywhere — it STARTS A NEW ONE from these lines (§11.3 calls it
            * the highest-value shortcut in the app). Filing it under "Share"
            * put a create action inside a send menu, which is the kind of
            * grouping that makes someone hunt for it.
            */}
          {can("invoice.create") ? (
            <Button
              variant="outline"
              nativeButton={false}
              render={<Link to={routes.invoiceCopy(invoice.id)} />}
            >
              <CopyIcon />
              {t("btn.makeACopy")}
            </Button>
          ) : null}
        </div>
      </header>

      {/*
        * A CANCELLED BILL SAYS SO LOUDLY AND FIRST. It keeps its number and
        * stays in the book (§17.4), which means it sits in the register beside
        * bills that stand — so on the document itself there must be no chance of
        * mistaking one for the other.
        */}
      {cancelled ? (
        <Alert variant="destructive">
          <TriangleAlertIcon />
          <AlertTitle>{t("invoice.lockedCancelledTitle")}</AlertTitle>
          <AlertDescription>
            {t("invoice.lockedCancelledHelp", { number: invoice.number })}
            {invoice.cancelReason === null ? null : (
              <span className="mt-2 block">
                {t("doc.cancelledReason", { reason: invoice.cancelReason })}
              </span>
            )}
          </AlertDescription>
        </Alert>
      ) : invoice.status === "DRAFT" ? (
        <Alert>
          <InfoIcon />
          <AlertTitle>{t("invoice.draftTitle")}</AlertTitle>
          <AlertDescription>{t("invoice.draftHelp")}</AlertDescription>
        </Alert>
      ) : (
        /*
         * §11.3 — said plainly, so "why can't I fix it?" is answered before it
         * is asked, and the copy route is offered in the same breath.
         *
         * WITH AN ICON, and not a warning one. This is a NOTE: the bill is
         * fine, and this explains how the app works. The cancelled branch above
         * uses `destructive` and a triangle because that one IS a warning —
         * giving both the same treatment would flatten the difference between
         * "this is normal" and "this document does not stand".
         */
        <Alert>
          <InfoIcon />
          <AlertTitle>{t("invoice.lockedTitle")}</AlertTitle>
          <AlertDescription>{t("invoice.lockedHelp")}</AlertDescription>
        </Alert>
      )}

      {/*
        * DIRECTLY UNDER THE BANNER THAT SAYS WHAT IS MISSING. The alert above
        * reads "waiting for its S.O. number"; the field that supplies it belongs
        * in the same breath, not at the foot of the page past the lines and the
        * totals.
        *
        * BOTH CONDITIONS, NEVER ONE. `can("invoice.edit_draft")` is §2.1's
        * authorisation — the ACCOUNTANT does not hold it — and `isEditable` is
        * the SERVER's answer about this document's state (true for a DRAFT
        * only). The status check beside them is belt and braces on the same
        * fact. The API asserts all of it again on the PUT, so these hide a
        * control and never authorise one.
        */}
      {invoice.status === "DRAFT" && invoice.isEditable && can("invoice.edit_draft") ? (
        <DraftHeaderCard invoice={invoice} />
      ) : null}

      {/*
        * RULE 138 — HIGH ON THE PAGE, WITH THE OTHER THINGS YOU MUST KNOW ABOUT
        * THIS BILL, and above the PDF rather than below the totals.
        *
        * The obvious home would be under `DocumentTotalsCard`, since the
        * threshold is a comparison against the grand total. That would be wrong.
        * "Goods over ₹1,00,000 may not move without an e-way bill" is not a
        * remark about the arithmetic — it is a condition on DISPATCH, and the
        * next thing the reader does on this screen is tap "View the bill" and
        * print it. A warning below the line items is a warning read after the
        * paper is in the printer.
        *
        * THE NOTIFICATION IS THE NUDGE; THE INVOICE IS THE RECORD. The alert
        * finds someone who is not looking at this bill. This card answers the
        * same question months later for someone who never saw the alert — and it
        * is where the number gets typed once the CA has been to the portal.
        *
        * IT RENDERS NOTHING ON NEARLY EVERY BILL. Below the threshold, with no
        * number recorded and no capability to record one, the component returns
        * null — so the ordinary ₹8,000 job looks exactly as it did before.
        */}
      <EwayBillCard invoice={invoice} />

      {pdf.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("doc.pdfFailedTitle")}</AlertTitle>
          <AlertDescription>
            {pdf.error instanceof ApiError ? pdf.error.message : t("doc.pdfFailedHelp")}
          </AlertDescription>
        </Alert>
      ) : null}

      {pdf.url === null ? null : <PdfPanel url={pdf.url} filename={pdf.filename} />}

      <DocumentFacts invoice={invoice} />

      <Card className="overflow-hidden py-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead>{t("lbl.description")}</TableHead>
                {/* "HSN" is the statutory acronym and is printed as HSN on the
                  * invoice itself in both languages, so it is not translated —
                  * `line.taxCodeField` is the word for the FIELD, not the
                  * column heading of a printed register. */}
                <TableHead>HSN</TableHead>
                <TableHead className="text-right">{t("lbl.quantity")}</TableHead>
                <TableHead className="text-right">{t("lbl.rate")}</TableHead>
                <TableHead className="text-right">{t("lbl.taxRate")}</TableHead>
                <TableHead className="text-right">{t("lbl.amount")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoice.lines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-muted-foreground py-8 text-center">
                    {t("doc.noLines")}
                  </TableCell>
                </TableRow>
              ) : (
                invoice.lines.map((line, index) => (
                  <LineRow key={line.id} line={line} index={index} />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <DocumentTotalsCard invoice={invoice} />

      {/*
        * §17.3 — BETWEEN THE TOTALS AND THE MONEY, WHICH IS EXACTLY WHERE IT
        * BELONGS.
        *
        * A credit note reduces the grand total immediately above it, and the
        * payments card immediately below then subtracts what it credited from
        * what is owed ("Credited back", from `summary.creditedPaise`). Read
        * downward the three cards are one sentence: this is the bill, this came
        * off it, this is what is left.
        *
        * IT IS ALSO THE ONLY ENTRY POINT TO THE FEATURE (rule 1) — a credit note
        * is raised from the bill it corrects, never from a blank form asking
        * which one. The card gates itself on `creditnote.view` /
        * `creditnote.create`, on the `credit_notes` flag (via the 404 its own
        * query gets while the module is off, §18.3) and on the bill being
        * ISSUED, and renders nothing at all when there is nothing to say.
        */}
      <CreditNotesOnInvoice invoice={invoice} />

      {/*
        * §17.5 — DIRECTLY UNDER THE TOTALS, because "still owed" is a statement
        * about the grand total immediately above it, and above the destructive
        * cancel card, which always stays last.
        *
        * NOTHING IT SHOWS IS ON THE INVOICE. A tax invoice carries no running
        * balance (§9.2 lists what the page must show), so the PDF is unchanged
        * in both directions. The card gates itself on `payment.view`, on the
        * `payments` flag (via the 404 its own query gets while the module is
        * off) and on there being something to say — a DRAFT with no receipts
        * renders nothing at all.
        */}
      <PaymentsCard
        invoiceId={invoice.id}
        invoiceNumber={invoice.number}
        customerId={invoice.customerId}
        /* The FROZEN snapshot (§4) — who was billed that day, not who the
         * customer row says they are today. */
        customerName={invoice.customer?.name ?? null}
      />

      {invoice.notes === null && invoice.terms === null ? null : (
        <Card>
          <CardHeader>
            <CardTitle>{t("invoice.wordsTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 text-sm">
            {invoice.notes === null ? null : (
              <div>
                <div className="text-muted-foreground text-xs">{t("lbl.notes")}</div>
                <p className="whitespace-pre-wrap">{invoice.notes}</p>
              </div>
            )}
            {invoice.terms === null ? null : (
              <div>
                <div className="text-muted-foreground text-xs">{t("lbl.terms")}</div>
                <p className="whitespace-pre-wrap">{invoice.terms}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/*
        * Cancelling is the one destructive action on this screen, so it sits at
        * the very bottom, apart from everything, gated on `invoice.cancel`.
        * `queryClient` is invalidated by the dialog on success — the register,
        * this document and the HOME SCREEN all change, since "billed this month"
        * stops counting a cancelled bill.
        */}
      {can("invoice.cancel") && !cancelled && invoice.status !== "DRAFT" ? (
        <CancelSection
          invoiceId={invoice.id}
          invoiceNumber={invoice.number}
          onCancelled={() => {
            /*
             * EVERYTHING, not just this document. Cancelling changes the row in
             * the register, the status here, AND the home screen — "billed this
             * month" stops counting it and the chart's column drops. A narrower
             * invalidation would leave the owner looking at a total that still
             * includes a bill he just cancelled.
             */
            void queryClient.invalidateQueries()
          }}
        />
      ) : null}
    </div>
  )
}

function BackLink(): React.JSX.Element {
  const { t } = useTranslation()
  return (
    <Link
      to={routes.invoices}
      className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1.5 text-sm"
    >
      <ArrowLeftIcon className="size-4" />
      {t("nav.invoices")}
    </Link>
  )
}

/**
 * The two of `PUT /invoices/:id`'s four accepted columns this screen sends.
 *
 * `null` CLEARS; a key left out is not written at all. The route also takes
 * `challanNumber` / `challanDate` — the CUSTOMER'S own delivery-note reference,
 * on the same §9.2 header row — and this card deliberately does not offer them:
 * a missing S.O. number is what strands a bill, and widening the form is how
 * "complete what conversion left out" quietly becomes "edit the header".
 */
interface DraftHeaderInput {
  soNumber: string | null
  soDate: string | null
}

/**
 * `PUT /invoices/:id` — completing a DRAFT bill's header (§7.1, §9.2).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT IS NOT `invoice.edit`, AND THERE IS NO SUCH CAPABILITY FOR ANYONE (§2.1).
 * The route is gated on `invoice.edit_draft`, refuses anything but a DRAFT with
 * a 409, and its body is `.strict()` with four keys — an unknown one is a 400,
 * not a silent ignore. Nothing it can reach has been issued to a customer.
 *
 * `null` CLEARS, A VALUE SETS. The server treats `null`, `""` and whitespace
 * alike as "clear this column"; a key that is not sent leaves the column alone.
 * This screen sends both fields every time, because the form shows both.
 * ─────────────────────────────────────────────────────────────────────────────
 */
function useCompleteDraftInvoice(id: number) {
  return useMutation({
    mutationFn: async (input: DraftHeaderInput) => {
      /*
       * THE SAME PATH AS THE `GET`. `endpoints.invoice(id)` is one spelling for
       * one resource — a second entry for the write is how `/invoices/:id` and
       * `/invoice/:id` end up in the same app.
       */
      return api.put<PreviewEnvelope<InvoiceDetail>>(endpoints.invoice(id), input)
    },
  })
}

/**
 * THE DRAFT BILL THAT IS NO LONGER STRANDED (§7.1).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHAT THIS FIXES. Converting a quotation without the customer's S.O. number
 * produces a DRAFT invoice. Its number is spent the moment it is raised — §6
 * keeps the series gap-free, so nothing gives that number back — and the S.O.
 * arrives weeks later with their purchase order. Until `PUT /invoices/:id`
 * existed there was no way to complete it: the bill sat in the register holding
 * a consumed number for ever.
 *
 * WHAT IT IS NOT. It is not an invoice editor. The lines, the totals, the
 * customer, the date, the number and the status are untouched and untouchable —
 * the request body is `.strict()` and names four columns. Two of them are here,
 * because the S.O. pair is what strands a bill; the challan pair the route also
 * accepts is left to the editor that owns those fields.
 *
 * IT DOES NOT ISSUE THE BILL EITHER, and the copy says so rather than implying
 * otherwise. The server deliberately leaves the status DRAFT: auto-issuing on
 * save would make a mistyped S.O. number permanently uncorrectable from that
 * instant, fixable only by cancellation or a credit note — a worse trap than the
 * one this removes.
 * ═════════════════════════════════════════════════════════════════════════════
 */
function DraftHeaderCard({ invoice }: { invoice: InvoiceDetail }): React.JSX.Element {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [soNumber, setSoNumber] = useState(invoice.soNumber ?? "")
  const [soDate, setSoDate] = useState(invoice.soDate ?? "")
  const save = useCompleteDraftInvoice(invoice.id)

  /*
   * The §9.2 pairing rule, the same one the convert dialog applies: a date
   * without its number would print an "S.O. Date" row above an empty "S.O. No."
   * row, and the server answers 400 for it — checked against the MERGED row, so
   * clearing the number while a date stands is refused too. Sending `null` for
   * both when the box is empty keeps the pair coherent by construction.
   */
  const hasSoNumber = soNumber.trim() !== ""

  return (
    <Card data-testid="invoice-draft-so">
      <CardHeader>
        <CardTitle className="text-base">{t("invoice.addSoTitle")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-muted-foreground text-sm">{t("invoice.addSoHelp")}</p>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invoice-so-number">{t("lbl.soNumber")}</Label>
            <Input
              id="invoice-so-number"
              value={soNumber}
              onChange={(event) => {
                setSoNumber(event.target.value)
              }}
              /* `so_number @db.VarChar(64)` — the schema caps it at the column. */
              maxLength={64}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invoice-so-date">{t("lbl.soDate")}</Label>
            <DateField
              id="invoice-so-date"
              value={soDate}
              onChange={setSoDate}
              clearable
              disabled={!hasSoNumber}
            />
          </div>
        </div>

        {save.isError ? (
          <Alert variant="destructive">
            <AlertDescription>
              {save.error instanceof ApiError ? save.error.message : t("err.somethingWentWrong")}
            </AlertDescription>
          </Alert>
        ) : null}

        <div>
          <Button
            disabled={save.isPending}
            onClick={() => {
              save.mutate(
                {
                  soNumber: hasSoNumber ? soNumber.trim() : null,
                  soDate: hasSoNumber && soDate !== "" ? soDate : null,
                },
                {
                  onSuccess: () => {
                    toast.success(t("invoice.addSoSaved"))
                    /*
                     * THE DETAIL *AND* THE REGISTER. `useInvoice` caches for
                     * five minutes on the explicit grounds that an issued
                     * invoice is immutable, so without an invalidation this
                     * screen would go on showing the bill as it was before the
                     * save. And `soNumber` is a column on the register row that
                     * `?q=` searches, so a list left alone would not find the
                     * bill by the number just typed.
                     *
                     * The `["invoices"]` prefix covers both and nothing else —
                     * a DRAFT counts toward no dashboard figure, so the home
                     * screen has nothing to re-read.
                     */
                    void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all })
                  },
                },
              )
            }}
          >
            {save.isPending ? t("msg.saving") : t("btn.save")}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * The PDF itself — inline on a big screen, a download on a phone.
 *
 * EXPORTED, AND KIND-AGNOSTIC. It takes an object URL and a filename and knows
 * nothing about invoices; §17.3's credit-note detail screen renders the same
 * panel with its own heading rather than a fourth copy of the `<object>`
 * fallback dance, which is the part that is easy to get subtly wrong (see the
 * note on `<object>` below — a mobile browser has no inline viewer and the
 * download link inside is the REAL path, not a fallback).
 */
export function PdfPanel({
  url,
  filename,
  /** Defaults to the bill's heading, so the three existing call sites are unchanged. */
  titleKey = "doc.pdfTitleBill",
}: {
  url: string
  filename: string | null
  titleKey?: string
}): React.JSX.Element {
  const { t } = useTranslation()

  return (
    <Card className="overflow-hidden py-0">
      <CardHeader className="flex flex-row items-center justify-between gap-3 py-4">
        <CardTitle>{t(titleKey)}</CardTitle>
        <Button variant="outline" nativeButton={false} render={<a href={url} download={filename ?? "invoice.pdf"} />}>
          <DownloadIcon />
          {t("btn.downloadPdf")}
        </Button>
      </CardHeader>
      <Separator />
      {/*
        * `<object>` rather than `<iframe>`: it degrades to its children when the
        * platform has no inline PDF viewer, which is every mobile browser. The
        * owner is on a phone, so the download link inside is the REAL path for
        * him, not a fallback — §11.1's "on a phone it opens in your PDF reader".
        */}
      <object data={url} type="application/pdf" className="hidden h-[70vh] w-full md:block">
        <p className="p-6 text-sm">{t("doc.pdfHelp")}</p>
      </object>
      <p className="text-muted-foreground p-4 text-sm md:hidden">{t("doc.pdfHelp")}</p>
    </Card>
  )
}

function DocumentFacts({ invoice }: { invoice: InvoiceDetail }): React.JSX.Element {
  const { t } = useTranslation()
  const formatDay = useFormatDay()
  const customer = invoice.customer

  /*
   * A FACT MAY CARRY A DESTINATION. `to` is set only where the app holds a REAL
   * row to open — never inferred from a typed reference. See the quotation and
   * challan notes below.
   */
  const facts: { label: string; value: string | null; to?: string }[] = [
    { label: t("lbl.gstin"), value: customer?.gstin ?? null },
    /*
     * THE BUYER'S PAN, exactly as the printed document derives it (§9.2).
     *
     * Registered → characters 3-12 of the GSTIN; unregistered → the Rule 114B
     * PAN typed on the customer. Never both: `customerSnapshot` writes
     * `pan: gstin === null ? customer.pan : null`, so the two are mutually
     * exclusive by construction and this `??` chain can only produce one.
     *
     * `panFromGstin` is the SHARED helper in `@steelbill/types`, the same one
     * the PDF uses — so the screen and the paper cannot disagree about a number
     * the buyer's accounts team will check.
     */
    { label: t("lbl.pan"), value: panFromGstin(customer?.gstin) ?? customer?.pan ?? null },
    { label: t("doc.placeOfSupply"), value: `${invoice.placeOfSupply} · ${customer?.stateName ?? ""}`.trim() },
    {
      label: t("doc.taxType"),
      // The server decided this (§6). Rendering it from the state codes here
      // would be a second implementation of `determineSupplyType`.
      value: invoice.supplyType === "INTER_STATE" ? t("doc.taxInter") : t("doc.taxIntra"),
    },
    { label: t("lbl.soNumber"), value: invoice.soNumber },
    { label: t("lbl.soDate"), value: invoice.soDate === null ? null : formatDay(invoice.soDate) },
    /*
     * NOT A LINK, AND MUST NOT BECOME ONE. §6: this column holds the CUSTOMER'S
     * OWN challan reference — their document, their numbering — and is
     * explicitly "unrelated" to this business's `CHL/` series. §17.7 has since
     * shipped, so there ARE challan rows to open now — which makes this MORE
     * dangerous rather than less: a text match against our own series would open
     * an unrelated movement of ours under the customer's reference. The bill's
     * real link to a challan is `linkedInvoiceId`, written from the other side.
     */
    { label: t("lbl.challanNumber"), value: invoice.challanNumber },
    {
      label: t("lbl.challanDate"),
      value: invoice.challanDate === null ? null : formatDay(invoice.challanDate),
    },
    /*
     * LINKED ONLY THROUGH `sourceQuoteId`, never by matching the text.
     *
     * `quotationRef` is a free-typed field: it may hold this business's own
     * quotation number, or a number from the customer's own paperwork, or a
     * note. `sourceQuoteId` is the foreign key §7.1's conversion writes, so it
     * is the only one that names a row we can actually open. A bill typed by
     * hand with "541/26" in the box gets the text and no link — which is
     * honest, because nothing here knows that 541/26 is ours.
     */
    {
      label: t("lbl.quotationRef"),
      value: invoice.quotationRef,
      to: invoice.sourceQuoteId === null ? undefined : routes.quotation(invoice.sourceQuoteId),
    },
    { label: t("lbl.contactName"), value: invoice.attnName },
    { label: t("lbl.vendorCode"), value: customer?.vendorCode ?? null },
  ].filter((fact) => fact.value !== null && fact.value !== "")

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("doc.factsTitle")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {customer === null ? null : (
          <address className="text-sm not-italic">
            {/*
              * THE BUYER'S NAME OPENS THEIR RECORD — the fastest way to fix a
              * wrong GSTIN or address after spotting it on a bill.
              *
              * IT SHOWS THE FROZEN SNAPSHOT AND LINKS TO THE LIVE ROW, and
              * those are different things (§4): the name here is what this
              * document recorded, while the destination is the customer as they
              * are today. That is the right pair — you read what was billed and
              * click through to correct what will be billed next — but it does
              * mean the screen you land on may show a different name.
              */}
            <div className="font-medium">
              <Link
                to={routes.customer(invoice.customerId)}
                className="hover:text-primary underline-offset-4 hover:underline"
              >
                {customer.name}
              </Link>
            </div>
            {[customer.addressLine1, customer.addressLine2, customer.city, customer.pincode]
              .filter((part) => part !== null && part !== "")
              .map((part, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: address lines have no id.
                <div key={index} className="text-muted-foreground">
                  {part}
                </div>
              ))}
          </address>
        )}

        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          {facts.map((fact) => (
            <div key={fact.label} className="flex justify-between gap-4 border-b pb-1">
              <dt className="text-muted-foreground">{fact.label}</dt>
              <dd className="text-right font-medium">
                {fact.to === undefined ? (
                  fact.value
                ) : (
                  <Link
                    to={fact.to}
                    className="hover:text-primary underline-offset-4 hover:underline"
                  >
                    {fact.value}
                  </Link>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  )
}

/**
 * One line of ANY document, as the detail screens print it.
 *
 * EXPORTED SINCE 2026-08-16 — it takes a `DocumentLine`, which is one shape on
 * all four kinds (§4: the kinds differ in their HEADER fields, never in a line),
 * and it already handles the two-level §9.1 hierarchy and the two unit
 * conversions that are easy to get wrong. §17.3's credit-note screen renders the
 * identical seven columns, and a copy of this would have been the place a fifth
 * pricing mode was learned three times and forgotten once.
 */
export function LineRow({ line, index }: { line: DocumentLine; index: number }): React.JSX.Element {
  const { t } = useTranslation()

  if (line.isGroupHeader) {
    return (
      <TableRow className="bg-muted/40">
        <TableCell className="tabular-nums">{line.lineCode ?? index + 1}</TableCell>
        <TableCell colSpan={5} className="font-medium">
          {line.description}
        </TableCell>
        <TableCell className="text-muted-foreground text-right text-xs">
          {t("a11y.headerAmount")}
        </TableCell>
      </TableRow>
    )
  }

  return (
    <TableRow>
      <TableCell className="text-muted-foreground tabular-nums">
        {line.lineCode ?? index + 1}
      </TableCell>
      <TableCell className={line.parentLineId === null ? "" : "pl-8"}>
        {line.description}
        {/*
          * §15(3)(a) — the line's own discount, under its description, exactly
          * where the printed sheet puts it (§9.2b). NOT its apportioned share of
          * a whole-bill discount: that is one negotiated figure shown once in
          * the totals card, and repeating each line's computed slice beside it
          * invites the reader to add the two and double-count.
          */}
        {line.discountPaise > 0 ? (
          <span className="text-muted-foreground block text-xs tabular-nums">
            {t("line.discountApplied", { amount: formatRupees(line.discountPaise) })}
          </span>
        ) : null}
      </TableCell>
      <TableCell className="tabular-nums">{line.hsn}</TableCell>
      <TableCell className="text-right tabular-nums whitespace-nowrap">
        {/*
          * MILLI-UNITS → units, the one division on this screen. `qtyMilli` is
          * an integer (`1000` is 1.000) precisely so no float ever reaches
          * storage; this converts for DISPLAY only and nothing downstream reads
          * it back.
          */}
        {(line.qtyMilli / 1000).toFixed(3).replace(/\.?0+$/, "")} {line.unitLabel}
      </TableCell>
      <TableCell className="text-right tabular-nums whitespace-nowrap">
        {formatRupees(line.ratePaise)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {/* BASIS POINTS → percent. `1800` is 18%, and the snapshot is what the
          * document was taxed at (§8.7) — never today's rate for this HSN. */}
        {line.gstRateBp / 100}%
      </TableCell>
      <TableCell className="text-right font-medium tabular-nums whitespace-nowrap">
        {formatRupees(line.lineTotalPaise)}
      </TableCell>
    </TableRow>
  )
}

function DocumentTotalsCard({ invoice }: { invoice: InvoiceDetail }): React.JSX.Element {
  const { t } = useTranslation()
  const { totals, supplyType } = invoice
  const interState = supplyType === "INTER_STATE"

  const rows: { label: string; paise: number; show: boolean }[] = [
    /*
     * §15(3)(a), in §9.2b's order — the same derivation the printed sheet
     * states, so the screen and the paper read alike. ABOVE the subtotal, not
     * below it: `subtotalPaise` is already NET and is the figure the AMOUNT
     * column sums to and the tax was charged on. Shown only when there is a
     * discount, which is almost never.
     */
    {
      label: t("doc.beforeDiscount"),
      paise: totals.grossSubtotalPaise,
      show: totals.totalDiscountPaise > 0,
    },
    {
      label: t("doc.lessDiscount"),
      paise: totals.totalDiscountPaise,
      show: totals.totalDiscountPaise > 0,
    },
    { label: t("lbl.subtotal"), paise: totals.subtotalPaise, show: true },
    // CGST and SGST for a same-state supply, IGST for a different state — never
    // all three. The server already decided which; this only renders it.
    { label: "CGST", paise: totals.cgstPaise, show: !interState },
    { label: "SGST", paise: totals.sgstPaise, show: !interState },
    { label: "IGST", paise: totals.igstPaise, show: interState },
    { label: t("lbl.roundOff"), paise: totals.roundOffPaise, show: totals.roundOffPaise !== 0 },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("doc.totalsTitle")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <dl className="ml-auto flex w-full max-w-sm flex-col gap-2 text-sm">
          {rows
            .filter((row) => row.show)
            .map((row) => (
              <div key={row.label} className="flex justify-between gap-6">
                <dt className="text-muted-foreground">{row.label}</dt>
                <dd className="tabular-nums">{formatRupees(row.paise)}</dd>
              </div>
            ))}
          <Separator />
          <div className="flex justify-between gap-6 text-base font-semibold">
            <dt>{t("lbl.grandTotal")}</dt>
            <dd className="tabular-nums">{formatRupees(totals.grandTotalPaise)}</dd>
          </div>
        </dl>

        {totals.amountInWords === "" ? null : (
          <div className="mt-2 border-t pt-3 text-sm">
            <span className="text-muted-foreground">{t("doc.amountInWords")}: </span>
            {/* Worded by the SERVER, so this page and the printed PDF cannot
              * word the same number differently. */}
            <span className="font-medium">{totals.amountInWords}</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Placed last and alone. Cancelling is terminal and keeps the number (§17.4).
 *
 * THE REASON IS MANDATORY AND THE BUTTON STAYS DISABLED WITHOUT IT. The API
 * requires 3–500 characters, and the accountant is the one who will ask why a
 * number in a gap-free series is marked cancelled. Letting it through empty
 * would push that question a year into the future with nobody able to answer it.
 *
 * THE CONSEQUENCES ARE SPELLED OUT BEFORE THE BUTTON, not after. The owner is
 * about to do the one irreversible thing in the app.
 */
function CancelSection({
  invoiceId,
  invoiceNumber,
  onCancelled,
}: {
  invoiceId: number
  invoiceNumber: string
  onCancelled: () => void
}): React.JSX.Element {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState("")
  const cancel = useCancelInvoice(String(invoiceId))

  const tooShort = reason.trim().length < 3

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        {/*
          * THE ICON IS THE POINT. Cancelling is the only destructive act on
          * this screen and it CANNOT BE UNDONE — the number is spent for ever
          * (§17.4). A heading that reads like every other card heading is how
          * someone taps it to "tidy up" a bill they meant to correct. Red, and
          * an octagon rather than the triangle the CANCELLED banner uses, so
          * the two are never confused: one is a door, the other is a state.
          */}
        <CardTitle className="text-destructive flex items-center gap-2 text-base">
          <OctagonAlertIcon aria-hidden="true" className="size-4 shrink-0" />
          {t("cancelInvoice.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-start gap-3">
        <p className="text-muted-foreground text-sm">{t("cancelInvoice.help")}</p>

        <AlertDialog open={open} onOpenChange={setOpen}>
          {/*
            * A destructive-looking trigger, because the dialog behind it is a
            * confirmation and not a form: by the time it opens he has decided.
            */}
          <AlertDialogTrigger render={<Button variant="outline" className="border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive" />}>
            <OctagonAlertIcon />
            {t("cancelInvoice.action")}
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("cancelInvoice.confirmTitle", { number: invoiceNumber })}
              </AlertDialogTitle>
              <AlertDialogDescription>{t("cancelInvoice.confirmHelp")}</AlertDialogDescription>
            </AlertDialogHeader>

            <ul className="text-muted-foreground flex list-disc flex-col gap-1.5 pl-5 text-sm">
              <li>{t("cancelInvoice.keepsNumber", { number: invoiceNumber })}</li>
              <li>{t("cancelInvoice.staysInBooks")}</li>
              <li>{t("cancelInvoice.cannotUndo")}</li>
            </ul>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cancel-reason">{t("cancelInvoice.reasonLabel")}</Label>
              <Textarea
                id="cancel-reason"
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value)
                }}
                maxLength={500}
                rows={3}
              />
              <p className="text-muted-foreground text-xs">{t("cancelInvoice.reasonHelp")}</p>
            </div>

            {cancel.isError ? (
              <Alert variant="destructive">
                <AlertDescription>
                  {cancel.error instanceof ApiError
                    ? cancel.error.message
                    : t("err.somethingWentWrong")}
                </AlertDescription>
              </Alert>
            ) : null}

            <AlertDialogFooter>
              <AlertDialogCancel render={<Button variant="outline" />}>
                {t("cancelInvoice.keepIt")}
              </AlertDialogCancel>
              <Button
                variant="destructive"
                disabled={tooShort || cancel.isPending}
                onClick={() => {
                  cancel.mutate(reason.trim(), {
                    onSuccess: () => {
                      setOpen(false)
                      toast.success(t("cancelInvoice.done", { number: invoiceNumber }))
                      onCancelled()
                    },
                  })
                }}
              >
                {t("cancelInvoice.confirmAction")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  )
}

function DetailSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <Skeleton className="h-4 w-20" />
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-48 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  )
}
